import { getParticipantName, getSportTerminology } from "./matchDisplayUtils";

const toNumericScore = (value) => {
  if (typeof value === "object" && value !== null) {
    return value.total || 0;
  }
  return value || 0;
};

export const extractScoresFromScoreData = (scoreData) => {
  if (!scoreData) return null;
  return {
    participant1: toNumericScore(scoreData.participant1),
    participant2: toNumericScore(scoreData.participant2),
  };
};

export const extractScoresFromMatchResponse = (matchData) => {
  if (!matchData) return null;
  if (matchData.scoreData) {
    return extractScoresFromScoreData(matchData.scoreData);
  }
  if (matchData.score_data) {
    const parsedScore =
      typeof matchData.score_data === "string"
        ? JSON.parse(matchData.score_data)
        : matchData.score_data;
    return extractScoresFromScoreData(parsedScore);
  }
  return null;
};

const toVersion = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

/**
 * The server's score_version for a match response, a score_data object or a
 * live_score_update payload. The server counts every live score write in
 * score_data.score_version; a stored score without one is version 0.
 * Returns null when there is no score data to read it from.
 */
export const extractScoreVersion = (source) => {
  if (!source) return null;
  if (source.scoreVersion !== undefined) return toVersion(source.scoreVersion);
  if (source.score_version !== undefined) return toVersion(source.score_version);
  let scoreData = source.scoreData ?? source.score_data;
  if (typeof scoreData === "string") {
    try {
      scoreData = JSON.parse(scoreData);
    } catch {
      scoreData = null;
    }
  }
  if (!scoreData || typeof scoreData !== "object") return null;
  return toVersion(scoreData.score_version) ?? 0;
};

/** Is this API error the server refusing a stale score write (another device scored first)? */
export const isScoreVersionConflict = (err) =>
  err?.response?.status === 409 && err?.response?.data?.code === "SCORE_VERSION_CONFLICT";

export const buildScorePayload = ({ match, participant, newScores, description }) => ({
  scoreData: newScores,
  eventData: {
    participantId: participant === "participant1" ? match?.participant1Id : match?.participant2Id,
    participantType: "user",
    eventTime: Math.floor(Date.now() / 1000),
    period: 1,
    description,
  },
});

export const buildResetScorePayload = ({ match, newScores, description, period }) => ({
  scoreData: newScores,
  eventData: {
    participantId: match?.participant1Id,
    participantType: "user",
    eventTime: Math.floor(Date.now() / 1000),
    period: period ?? 1,
    description: description || "Match score reset to 0-0",
  },
});

export const buildScoreEventLogEntry = ({ type, participant, newScores, match, description }) => {
  if (type === "score_reset") {
    return {
      id: `score-reset-${Date.now()}-${Math.random()}`,
      type,
      participant: "both",
      // A reset applies to both sides — never show a single player's name.
      hideParticipantName: true,
      description: description || "Score reset to 0–0",
      timestamp: new Date().toLocaleTimeString(),
    };
  }

  if (type === "game_start") {
    // A new game / set started in a multi-game match (badminton, table
    // tennis, volleyball). The point score goes back to 0–0 by rule.
    return {
      id: `game-start-${Date.now()}-${Math.random()}`,
      type,
      participant: "both",
      hideParticipantName: true,
      description: description || "New game started",
      timestamp: new Date().toLocaleTimeString(),
    };
  }

  const participantName = getParticipantName(match, participant);
  const terminology = getSportTerminology(match?.sport_name);
  const isIncrease = type === "score_increase";

  return {
    id: `${isIncrease ? "score-increase" : "score-decrease"}-${Date.now()}-${Math.random()}`,
    type,
    participant,
    participantName,
    score: newScores[participant],
    sportTerminology: terminology,
    description: isIncrease
      ? `${terminology} scored by ${participantName}`
      : `Score correction for ${participantName}`,
    hideParticipantName: true,
    timestamp: new Date().toLocaleTimeString(),
  };
};
